import {
  DEFAULT_APP_LOCALE,
  localized,
  localeTag,
  romanianCountLabel,
  type AppLocale,
  type Localized,
} from "./locale";

export type TranslationKey = keyof typeof import("./locales/ru").ru;
type TranslationDictionary = Readonly<Record<TranslationKey, string>>;

const localeLoaders: Record<
  AppLocale,
  () => Promise<TranslationDictionary>
> = {
  ru: async () => (await import("./locales/ru")).ru,
  ro: async () => (await import("./locales/ro")).ro,
  uk: async () => (await import("./locales/uk")).uk,
};

const localeCache: Partial<Record<AppLocale, TranslationDictionary>> = {};
let activeLocale: AppLocale = DEFAULT_APP_LOCALE;
let activeMessages: TranslationDictionary | null = null;

async function loadLocaleMessages(
  locale: AppLocale,
): Promise<TranslationDictionary> {
  const cached = localeCache[locale];
  const messages = cached ?? (await localeLoaders[locale]());
  localeCache[locale] = messages;
  return messages;
}

export async function preloadLocale(locale: AppLocale): Promise<void> {
  await loadLocaleMessages(locale);
}

export async function activateLocale(locale: AppLocale): Promise<void> {
  const messages = await loadLocaleMessages(locale);
  activeLocale = locale;
  activeMessages = messages;
}

export function getActiveLocale(): AppLocale {
  return activeLocale;
}

export function t(
  key: TranslationKey,
  variables: Record<string, string | number> = {},
): string {
  if (!activeMessages) {
    throw new Error("Translations must be activated before rendering");
  }
  return activeMessages[key].replace(
    /\{([a-zA-Z0-9_]+)\}/g,
    (_, name: string) => String(variables[name] ?? `{${name}}`),
  );
}

function translationKey(value: string | undefined): TranslationKey | null {
  return value && activeMessages && value in activeMessages
    ? (value as TranslationKey)
    : null;
}

export function applyDocumentTranslations(root: ParentNode = document): void {
  document.documentElement.lang = activeLocale;
  // Static website routes own their title. Translating shared controls must not
  // replace the store/product title with the generic Mini App title.
  if (!document.querySelector("#kiloprice-route-context")) {
    document.title = t("documentTitle");
  }
  root.querySelectorAll<HTMLElement>("[data-i18n]").forEach((element) => {
    const key = translationKey(element.dataset.i18n);
    if (key) {
      element.textContent = t(key);
    }
  });
  root.querySelectorAll<HTMLElement>("[data-i18n-aria-label]").forEach((element) => {
    const key = translationKey(element.dataset.i18nAriaLabel);
    if (key) {
      element.setAttribute("aria-label", t(key));
    }
  });
  root.querySelectorAll<HTMLElement>("[data-i18n-title]").forEach((element) => {
    const key = translationKey(element.dataset.i18nTitle);
    if (key) {
      element.setAttribute("title", t(key));
    }
  });
  root.querySelectorAll<HTMLInputElement>("[data-i18n-placeholder]").forEach((element) => {
    const key = translationKey(element.dataset.i18nPlaceholder);
    if (key) {
      element.placeholder = t(key);
    }
  });
  root.querySelectorAll<HTMLMetaElement>("[data-i18n-content]").forEach((element) => {
    const key = translationKey(element.dataset.i18nContent);
    if (key) {
      element.content = t(key);
    }
  });
}

export function formatUiNumber(value: number): string {
  return value.toLocaleString(localeTag(activeLocale));
}

function russianCountLabel(
  value: number,
  forms: readonly [string, string, string],
): string {
  const absolute = Math.abs(value);
  const modulo100 = absolute % 100;
  const modulo10 = absolute % 10;
  if (modulo100 >= 11 && modulo100 <= 14) {
    return forms[2];
  }
  if (modulo10 === 1) {
    return forms[0];
  }
  if (modulo10 >= 2 && modulo10 <= 4) {
    return forms[1];
  }
  return forms[2];
}

const COUNT_LABELS: Record<
  "product" | "offer" | "catalog",
  Localized<(value: number) => string>
> = {
  product: {
    ru: (value) => russianCountLabel(value, ["товар", "товара", "товаров"]),
    ro: (value) => romanianCountLabel(value, "product"),
    uk: (value) =>
      russianCountLabel(value, ["товар", "товари", "товарів"]),
  },
  offer: {
    ru: (value) => russianCountLabel(value, [
      "предложение",
      "предложения",
      "предложений",
    ]),
    ro: (value) => romanianCountLabel(value, "offer"),
    uk: (value) =>
      russianCountLabel(value, ["пропозиція", "пропозиції", "пропозицій"]),
  },
  catalog: {
    ru: (value) => russianCountLabel(value, [
      "каталог",
      "каталога",
      "каталогов",
    ]),
    ro: (value) => romanianCountLabel(value, "catalog"),
    uk: (value) =>
      russianCountLabel(value, ["каталог", "каталоги", "каталогів"]),
  },
};

export function productWord(value: number): string {
  return localized(activeLocale, COUNT_LABELS.product)(value);
}

export function offerWord(value: number): string {
  return localized(activeLocale, COUNT_LABELS.offer)(value);
}

export function catalogWord(value: number): string {
  return localized(activeLocale, COUNT_LABELS.catalog)(value);
}
